import {IShopRating, IShopRatingSummary, RatingsFactory} from "../../typescript-library";
import React, {Fragment, PropsWithChildren, useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import RatingFilterMobile from "./RatingFilter/RatingFilterMobile";
import RatingFilter from "./RatingFilter/RatingFilter";
import Drawer from "../lib/form/Drawer/Drawer";
import RatingStars from "../lib/ratingStars/RatingStars";
import {useTranslation} from "react-i18next";
import ShopRatingComments from "./ShopRatingComments/ShopRatingComments";
import ShopRatingSummary from "./ShopRatingSummary/ShopRatingSummary";
import {getShopId} from "../lib/getShopId";
import {useMediaQuery} from "react-responsive";
import styles from "./ShopRatingSection.module.scss";
import genericStyles from "../lib/GenericStyles.module.scss";
import {BrowserStorage} from "../../storage/BrowserStorage";

export enum ShowFilter {
  all = "all",
  one = "one",
}

export enum SortFilter {
  newest = "newest",
  best = "best",
  worst = "worst",
}

export type FilterState = {filterSet: Set<number>; show: keyof typeof ShowFilter; sort: keyof typeof SortFilter};

type IShopRatingSectionState = {
  summary?: IShopRatingSummary;
  summaryWithAssociatedPointTransactions?: IShopRatingSummary;
  sum: number;
  sumPoints: number;
  allComments: IShopRating[];
  itemComments?: IShopRating[];
  filteredComments: IShopRating[];
  filter: FilterState;
};

export const initialFilterState: FilterState = {
  show: ShowFilter.all,
  sort: SortFilter.newest,
  filterSet: new Set<number>(),
};

const browserStorage = new BrowserStorage<IShopRatingSectionState>({
  type: "localStorage",
  dataIdentifier: "shopRating",
  toBeCompressed: true,
  hoursToCache: 1,
});

const ShopRatingSection: React.FC<{itemIds: number[]; headlineElement: HTMLElement | null}> = ({
  itemIds,
  headlineElement,
}) => {
  /* combines the shop ID and first item ID as a unique session storage identifier */
  const storageKey = getShopId().toString() + "-" + itemIds[0].toString();
  const storedRatings = browserStorage.read(storageKey);
  const initialState: IShopRatingSectionState = {
    ...(storedRatings ?? {
      allComments: [],
      filteredComments: [],
      sum: 0,
      sumPoints: 0,
    }),
    filter: initialFilterState,
  };
  const headlineRef = useRef<HTMLDivElement>(null);
  const containerDiv = document.getElementById("ProductRatingAccordionItem") as HTMLDivElement;
  const isOpenInit = containerDiv ? containerDiv.classList.contains("active") : false;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [state, setState] = useState<IShopRatingSectionState>(initialState);
  const [isOpen, setIsOpen] = useState(isOpenInit);
  const {t} = useTranslation();
  const isMobile = useMediaQuery({maxWidth: 767});

  useEffect(() => {
    const ratingsButtons = document.querySelectorAll(".ga-go-to-ratings");
    if (ratingsButtons.length) {
      ratingsButtons.forEach((ratingsButton) => {
        ratingsButton.addEventListener("click", handleRatingsClick);
      });
    }

    if (!storedRatings) {
      void getCommentsSummary();
    }

    return () => {
      if (ratingsButtons.length) {
        ratingsButtons.forEach((ratingsButton) => {
          ratingsButton.removeEventListener("click", handleRatingsClick);
        });
      }
    };
  }, []);

  useEffect(() => {
    if (state.filter.show === "all") {
      setState({...state, itemComments: undefined});
    } else {
      const itemId = document.querySelector(".product-attributes > table tr > td:nth-of-type(2)")?.innerHTML;
      if (itemId) {
        const commentsForItem = state.allComments.filter((comment) => comment.itemId === Number(itemId));
        setState({...state, itemComments: commentsForItem, filteredComments: commentsForItem});
      }
    }
  }, [state.filter.show]);

  const getCommentsSummary = async () => {
    const data = await RatingsFactory.getRatings(getShopId(), {itemIds, offset: 0, limit: 1, summary: true});
    let sum = 0;
    let sumPoints = 0;
    if (data && data.summary) {
      Object.keys(data.summary).forEach((key) => {
        if (data.summary) {
          sum += data.summary[key];
        }
      });
    }

    if (data && data.summaryWithAssociatedPointTransactions) {
      Object.keys(data.summaryWithAssociatedPointTransactions).forEach((key) => {
        if (data.summaryWithAssociatedPointTransactions) {
          sumPoints += data.summaryWithAssociatedPointTransactions[key];
        }
      });
    }
    setState((prevState) => {
      const newState = {
        ...prevState,
        sum,
        summary: data.summary,
        summaryWithAssociatedPointTransactions: data.summaryWithAssociatedPointTransactions,
        sumPoints,
      };
      /* stores state in session storage to reduce api calls */
      browserStorage.write(storageKey, newState);

      return newState;
    });
  };

  const getAllComments = async () => {
    const data = await RatingsFactory.getRatings(getShopId(), {
      itemIds,
      offset: 0,
      limit: state.sum,
    });
    setState((prevState) => {
      const newState = {
        ...prevState,
        allComments: data.ratings,
        filteredComments: data.ratings,
      };
      /* updates state in session storage to reduce api calls */
      browserStorage.write(storageKey, newState);

      return newState;
    });
  };

  const handleRatingsClick = () => {
    headlineRef.current?.scrollIntoView({behavior: "smooth"});
    headlineRef.current?.focus();

    if (!isOpen) {
      togglePanel();
    }
  };

  const handleToggleClick = () => {
    togglePanel();

    if (containerDiv && !isOpen) {
      const accTop = containerDiv.parentElement?.getBoundingClientRect().y;
      if (accTop && accTop < 0) {
        window.scroll(0, window.scrollY + accTop);
      }
    }
  };

  // expand or collapse accordion panel content
  const togglePanel = () => {
    if (containerDiv) {
      setIsOpen(!isOpen);
      containerDiv.classList.toggle("active");
    }

    if (state.sum > state.allComments.length) {
      void getAllComments();
    }
  };

  const {summary} = state;
  if (!summary) {
    return null;
  }
  const average =
    (5 * summary["5"] + 4 * summary["4"] + 3 * summary["3"] + 2 * summary["2"] + summary["1"]) / state.sum;

  const CommentsSummary = () => {
    return <span>({state.sum ? t("rating.reviews--sum", {sum: state.sum}) : t("general.loading")})</span>;
  };

  /* Hijacks the rating stars info below the PDP headline */
  const RatingStarsRow: React.FC = () => {
    if (!headlineElement) {
      return null;
    }
    const StarsRow = () => {
      const [divClass, setDivClass] = useState<string>(styles.ratingLink);
      useEffect(() => {
        setDivClass((prevState) => `${prevState} ${styles.visible}`);
      }, []);

      return (
        <div className={divClass}>
          <RatingStars rating={Math.round(average * 2)} size={"medium"} />
          <button onClick={handleRatingsClick}>
            {state.sum ? t("rating.reviews--sum", {sum: state.sum}) : t("general.loading")}
            {state.sumPoints > 0 && <sup className={genericStyles.footnoteReference}>**&nbsp;</sup>}
          </button>
        </div>
      );
    };

    return ReactDOM.createPortal(<StarsRow />, headlineElement);
  };

  const CancelFilterButton: React.FC<PropsWithChildren<{onCancel: () => void; className?: string}>> = ({
    children,
    className,
    onCancel,
  }) => (
    <button className={[styles.cancelFilterButton, className].join(" ")} onClick={onCancel}>
      {children}
      <span className={`icon icon-close ${styles.closeIcon}`} />
    </button>
  );

  return (
    <>
      <RatingStarsRow />
      <h2 ref={headlineRef} className="accordion__item__headline">
        <button aria-expanded={isOpen} onClick={handleToggleClick}>
          <div className={styles.title}>
            <span>{t("rating.reviews")}</span>
            <RatingStars rating={Math.round(average * 2)} size={"medium"} />
            {!isMobile ? <CommentsSummary /> : <span>({state.sum}) </span>}
            {state.sumPoints > 0 && <sup className={genericStyles.footnoteReference}>**&nbsp;</sup>}
          </div>
          <i className="icon icon-plus--blue" aria-hidden="true">
            &nbsp;
          </i>
        </button>
      </h2>
      <div className="accordion__item__content" hidden={!isOpen}>
        <div>
          <ShopRatingSummary
            summary={summary}
            filterSet={state.filter.filterSet}
            onFilterChange={(filterSet) => {
              setState({...state, filter: {...state.filter, filterSet}});
            }}
          />
          <div className={`${styles.legitNote} ${genericStyles.footnote}`}>
            {state.sumPoints > 0 && <sup className={genericStyles.footnoteIcon}>**&nbsp;</sup>}
            <span>
              {state.sumPoints > 0 && (
                <>
                  {t("rating.connoxPointsReviews--sumPoints--sum", {sumPoints: state.sumPoints, sum: state.sum})}
                  &nbsp;
                </>
              )}
              {t("rating.verifiedPurchaseReviews")}.&nbsp;
              <a href={t("url.productReviewLink")} target="_blank" rel="noreferrer" title={t("rating.reviewRules")}>
                {t("general.moreInformation")}
              </a>
            </span>
          </div>
          {!isMobile && (
            <RatingFilter
              defaultState={{show: state.filter.show, sort: state.filter.sort}}
              filteredComments={state.filteredComments.length}
              onChange={(filterState) => {
                setState({...state, filter: {...state.filter, ...filterState}});
              }}
            />
          )}

          {isMobile && (
            <Fragment>
              <button
                className={`icon icon-filter ${styles.filterIcon}`}
                onClick={() => {
                  setDrawerOpen(true);
                }}
              >
                {t("product.sortAndFilter")}
              </button>
              <div>
                {Array.from(state.filter.filterSet).map((item: number) => {
                  return (
                    <CancelFilterButton
                      key={item}
                      onCancel={() => {
                        const updatedSet = state.filter.filterSet;
                        updatedSet.delete(item);
                        setState({...state, filter: {...state.filter, filterSet: updatedSet}});
                      }}
                    >
                      {t("rating.stars--star", {star: item})}
                    </CancelFilterButton>
                  );
                })}
                {state.filter.show === "one" && (
                  <CancelFilterButton
                    onCancel={() => {
                      setState({...state, filter: {...state.filter, show: initialFilterState.show}});
                    }}
                  >
                    {t("product.selectedVariant")}
                  </CancelFilterButton>
                )}
              </div>
            </Fragment>
          )}

          <Drawer
            className={styles.drawerWrapper}
            open={drawerOpen}
            onClose={() => {
              setDrawerOpen(false);
            }}
          >
            <RatingFilterMobile
              filterState={state.filter}
              onChange={(filterState) => {
                setState({...state, filter: {...state.filter, ...filterState}});
                setDrawerOpen(false);
              }}
              onReset={() => {
                setState((prevState) => {
                  const updatedSet = state.filter.filterSet;
                  updatedSet.clear();

                  return {...prevState, filter: {...initialFilterState, filterSet: updatedSet}};
                });
                setDrawerOpen(false);
              }}
            />
          </Drawer>
          <ShopRatingComments
            comments={state.itemComments ? state.itemComments : state.allComments}
            filter={state.filter}
            onFilterComments={(comments) => {
              setState({...state, filteredComments: comments});
            }}
          />
        </div>
      </div>
    </>
  );
};

export default ShopRatingSection;
